import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { fabric } from 'fabric';
import { isEmpty, includes } from 'lodash';
import styles from '../../../styles/components/CanvasEditor.scss';

import {
  registerCanvas,
  updateOverlay,
  updateTool,
} from '../../../redux/actions/canvas_editor';

import { getStatusColor } from './Task/taskStatusColors';

import CursorTooltip from './CursorTooltip/CursorTooltip';

import { initFabricOverlay } from '../utils/initFabricOverlay';
import { initOSD } from '../utils/initOSD';
import { clampObjectPosition, handleBeforeRender } from '../utils/fabricUtils';

import { ensureArrowHead, setThicknessContext } from '../helpers/fabricHelpers';

export default function Viewer2({
  source,
  onSubmit,
  annotations,
  pinnedTasks,
  sourceType,
  disabled,
  onViewerReady,
  onFabricOverlayReady,
}) {
  const viewerRef = useRef(null);
  const fabricOverlayRef = useRef(null);
  const isLoadingRef = useRef(true);

  useEffect(() => {
    if (!source || !viewerRef.current) return;

    if (disabled) {
      initFabricOverlay();
      const viewer = initOSD(viewerRef, source, sourceType);

      if (!viewer) return;

      const fabricOverlay = viewer.fabricjsOverlay({
        scale: 1,
        static: true,
      });

      fabricOverlayRef.current = fabricOverlay;
      onViewerReady?.(viewer);
      onFabricOverlayReady?.(fabricOverlay);

      return () => {
        fabricOverlay.fabricCanvas()?.dispose?.();
        viewer.destroy();
        fabricOverlayRef.current = null;
      };
    }

    // init Fabric/OSD
    initFabricOverlay();
    const viewer = initOSD(viewerRef, source, sourceType);

    if (!viewer) {
      return;
    }

    onViewerReady?.(viewer);

    const fabricOverlay = viewer.fabricjsOverlay({
      scale: 1,
    });

    fabricOverlayRef.current = fabricOverlay;
    onFabricOverlayReady?.(fabricOverlay);

    const canvas = fabricOverlay.fabricCanvas();

    setThicknessContext({
      mode: 'osd',
    });

    // Add toolbar element to OSD viewport
    // const toolbarElement = document.getElementById('toolbar');
    // if (toolbarElement && viewer) {
    //   viewer.container.appendChild(toolbarElement);
    // }

    // Set overlay to be visible in other components like Draw, Circle...
    registerCanvas(1, canvas); // assuming single canvas with id 0

    updateOverlay({
      fabricOverlay,
      viewer,
    });

    updateTool({ tool: 'hand' });

    let objectWasModified = false;
    let objectWasAdded = false;
    const handleMouseUp = () => {
      if (objectWasModified || objectWasAdded) {
        onSubmit(fabricOverlay, objectWasAdded);
        objectWasModified = false;
        objectWasAdded = false;
      }
    };

    const handleObjectMoving = (e) => {
      objectWasModified = true;
      clampObjectPosition(e.target, viewer, source);
    };

    const handleObjectScaling = (e) => {
      objectWasModified = true;
      clampObjectPosition(e.target, viewer, source);
    };

    const handleObjectAdded = (e) => {
      if (
        isLoadingRef.current ||
        e.target?.isTemporary ||
        includes(
          ['controlpoint', 'highlight', 'textbox', 'arrow-head'],
          e.target?.objectType,
        )
      )
        return;

      if (
        includes(
          ['polygon', 'task', 'plan', 'specification', 'arrow'],
          e.target?.objectType,
        )
      ) {
        return onSubmit(fabricOverlay, true);
      }

      objectWasAdded = true;
    };

    const handleBatchRemoved = (e) => {
      onSubmit(fabricOverlay, true);
    };

    const handleObjectModified = (e) => {
      const source = e.source;
      const target = e.target;

      if (target?.objectType === 'textbox') {
        onSubmit(fabricOverlay, false);
        return;
      }

      if (source === 'toolbar') {
        onSubmit(fabricOverlay, false);
      }
    };

    const handleBeforeRenderWrapped = () => handleBeforeRender(viewer, canvas);

    canvas.on('mouse:up', handleMouseUp);
    canvas.on('object:moving', handleObjectMoving);
    canvas.on('object:added', handleObjectAdded);
    canvas.on('object:scaling', handleObjectScaling);
    //canvas.on('text:editing:exited', handleObjectExited);
    canvas.on('before:render', handleBeforeRenderWrapped);
    canvas.on('batch:removed', handleBatchRemoved);
    canvas.on('object:modified', handleObjectModified);

    return () => {
      objectWasModified = false;

      canvas.off('mouse:up', handleMouseUp);
      canvas.off('object:moving', handleObjectMoving);
      canvas.off('object:added', handleObjectAdded);
      canvas.off('object:scaling', handleObjectScaling);
      canvas.off('before:render', handleBeforeRenderWrapped);
      canvas.off('batch:removed', handleBatchRemoved);
      canvas.off('object:modified', handleObjectModified);

      if (viewer) {
        viewer.destroy();
      }
      fabricOverlayRef.current = null;
      //updateTool({ tool: '' });
    };
  }, [
    source,
    sourceType,
    disabled,
    onViewerReady,
    onFabricOverlayReady,
  ]);

  useEffect(() => {
    if (!fabricOverlayRef.current) return;

    const canvas = fabricOverlayRef.current.fabricCanvas();
    isLoadingRef.current = true;
    canvas.clear();

    const promises = (annotations?.objects ?? []).map(
      (annotation) =>
        new Promise((resolve) => {
          fabric.util.enlivenObjects([annotation], (objects) => {
            objects.forEach((obj) => {
              if (obj.objectType === 'line' || obj.objectType === 'arrow') {
                const dx = Math.abs(obj.x1);
                const dy = Math.abs(obj.y1);
                const { left, top } = obj;

                obj.x1 = obj.x1 + left + dx;
                obj.x2 = obj.x2 + left + dx;
                obj.y1 = obj.y1 + top + dy;
                obj.y2 = obj.y2 + top + dy;

                obj.left = left;
                obj.top = top;
                obj.startLeft = left;
                obj.startTop = top;

                obj.setCoords();
              }

              if (obj.objectType === 'path') {
                obj.lockScalingX = true;
                obj.lockScalingY = true;
                obj.hasControls = true;
                obj.hasBorders = true;
                obj.perPixelTargetFind = true;

                obj.setCoords();
              }

              if (obj.objectType === 'task') {
                const status = pinnedTasks?.[obj.taskID]?.status;

                if (status) {
                  const color = getStatusColor(status);

                  obj.fill = color;
                  obj.stroke = color;
                }
              }

              if (disabled) {
                obj.set({
                  selectable: false,
                  evented: false,
                  hasControls: false,
                  hasBorders: false,
                });
              }

              canvas.add(obj);

              if (obj.objectType === 'arrow') {
                ensureArrowHead(obj);
              }
            });

            resolve();
          });
        }),
    );

    Promise.all(promises).then(() => {
      isLoadingRef.current = false;
      canvas.requestRenderAll();

      //updateTool({ tool: 'hand' });
    });
  }, [annotations, source, disabled]);

  return (
    <div
      ref={viewerRef}
      style={{ width: '100%', height: '100%' }}
      className={styles.viewer}
    >
      {!disabled && <CursorTooltip plan={source} />}
    </div>
  );
}

Viewer2.propTypes = {
  source: PropTypes.object,
};
